import {
	CheckIcon,
	ClockIcon,
	HelpCircleIcon,
	PlusIcon,
	RefreshCwIcon,
	SettingsIcon,
	ShuffleIcon,
	XIcon,
} from "lucide-react";
import {
	type ComponentProps,
	type ReactNode,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";

type MoveGridProps = {
	offsets: readonly Coord[];
	color: string;
	radius: number;
};

function MoveGrid({ offsets, color, radius }: MoveGridProps) {
	const size = radius * 2 + 1;
	return (
		<div
			className="grid gap-0.5"
			style={{ gridTemplateColumns: `repeat(${size}, 4px)` }}
		>
			{Array.from({ length: size * size }, (_, idx) => {
				const dx = (idx % size) - radius;
				const dy = radius - Math.floor(idx / size);
				const isAttack = offsets.some(([ox, oy]) => ox === dx && oy === dy);
				const isCenter = dx === 0 && dy === 0;
				return (
					<div
						key={idx}
						className={cn(
							"size-1 rounded-full",
							!isAttack && (isCenter ? "bg-slate-600" : "bg-slate-200"),
						)}
						style={isAttack ? { background: color } : undefined}
					/>
				);
			})}
		</div>
	);
}

type PieceInfoProps = { kind: PieceKind; color: string };

function PieceInfo({ kind, color }: PieceInfoProps) {
	return (
		<>
			<span className="text-2xl leading-none" style={{ color }}>
				{PIECES[kind].symbol}
			</span>
			<span className="flex-1 text-left text-sm capitalize">{kind}</span>
			<MoveGrid
				offsets={PIECES[kind].offsets}
				color={color}
				radius={MAX_LEAP_RADIUS}
			/>
		</>
	);
}

type BoardPreviewProps = {
	side: number;
	board: Uint8Array;
	step: number;
	currentIndex: number;
	size?: number;
};

function BoardPreview({
	side,
	board,
	step,
	currentIndex,
	size = 80,
}: BoardPreviewProps) {
	const half = (side - 1) / 2;
	const cellPx = size / side;
	const cells = buildSpiralCoords(side * side).map(([x, y]) => ({
		idx: (half - y) * side + (x + half),
		cx: (x + half + 0.5) * cellPx,
		cy: (half - y + 0.5) * cellPx,
	}));

	return (
		<div className="relative shrink-0">
			<svg width={size} height={size}>
				<polyline
					points={cells.map((c) => `${c.cx},${c.cy}`).join(" ")}
					fill="none"
					stroke="#e2e8f0"
					strokeWidth={1.2}
				/>
				{cells.map(({ idx, cx, cy }, i) => {
					const v = board[idx] ?? 0;
					if (v === 0)
						return <circle key={i} cx={cx} cy={cy} r={1.5} fill="#94a3b8" />;
					const isCurrent = idx === currentIndex;
					return (
						<circle
							key={i}
							cx={cx}
							cy={cy}
							r={cellPx * (isCurrent ? 0.38 : 0.3)}
							fill={colorAt(v - 1)}
							opacity={isCurrent ? 1 : 0.5}
						/>
					);
				})}
			</svg>
			<div className="absolute -top-1.5 -left-1.5 flex size-5 items-center justify-center rounded-full bg-slate-800 font-semibold text-[10px] text-white shadow-sm ring-2 ring-white">
				{step}
			</div>
		</div>
	);
}

const BUTTON_VARIANTS = {
	primary: "bg-slate-800 text-white hover:bg-slate-700",
	secondary: "bg-slate-100 text-slate-700 hover:bg-slate-200",
	ghost: "text-slate-400 hover:bg-slate-100 hover:text-slate-600",
	outline: "bg-white text-slate-500 ring-1 ring-slate-200 hover:text-slate-700",
};

type ButtonProps = ComponentProps<"button"> & {
	variant?: keyof typeof BUTTON_VARIANTS;
};

function Button({ variant = "ghost", className, ...props }: ButtonProps) {
	return (
		<button
			{...props}
			className={cn(
				"flex shrink-0 items-center justify-center gap-2 rounded-md px-2 py-1.5 font-medium text-sm transition-all disabled:cursor-not-allowed disabled:opacity-50",
				BUTTON_VARIANTS[variant],
				className,
			)}
		/>
	);
}

type SidebarProps = {
	open: boolean;
	isMobile: boolean;
	title: string;
	onClose: () => void;
	children: ReactNode;
};

function Sidebar({ open, isMobile, title, onClose, children }: SidebarProps) {
	return (
		<div
			className={cn(
				"flex shrink-0 flex-col border-slate-200 border-l bg-white shadow-lg transition-all duration-300 ease-in-out",
				isMobile ? "fixed inset-0 z-50" : "w-96",
				open ? "translate-x-0" : isMobile ? "translate-x-full" : "-mr-96",
			)}
		>
			<div className="flex shrink-0 items-center justify-between border-slate-100 border-b px-4 py-3">
				<span className="font-semibold text-slate-700 text-sm">{title}</span>
				<Button onClick={onClose} className="size-7">
					<XIcon size={16} />
				</Button>
			</div>
			{children}
		</div>
	);
}

type SettingsProps = {
	side: number;
	onSideChange: (value: number) => void;
	pieces: PieceEntry[];
	onAddPiece: (kind: PieceKind, colorIndex: number) => void;
	onRemovePiece: (index: number) => void;
	onClearPieces: () => void;
	onGenerate: () => void;
	onRandom: () => void;
	generating: boolean;
};

function Settings({
	side,
	onSideChange,
	pieces,
	onAddPiece,
	onRemovePiece,
	onClearPieces,
	onGenerate,
	onRandom,
	generating,
}: SettingsProps) {
	const [addingPiece, setAddingPiece] = useState(false);
	const [adderColorIndex, setAdderColorIndex] = useState(0);

	const usedColorIndices = new Set(pieces.map((p) => p.colorIndex));
	const allColorsUsed = usedColorIndices.size >= PALETTE.length;
	const adderColor = colorAt(adderColorIndex);

	const openAdder = () => {
		setAdderColorIndex(PALETTE.findIndex((_, i) => !usedColorIndices.has(i)));
		setAddingPiece(true);
	};

	return (
		<>
			<div className="shrink-0 px-4 py-4">
				<label className="mb-2 block font-medium text-slate-400 text-xs uppercase tracking-wider">
					Board size
				</label>
				<div className="flex items-baseline justify-between">
					<span className="font-mono font-semibold text-base text-slate-700 tabular-nums">
						{side}×{side}
					</span>
					<span className="text-slate-400 text-xs">
						~ {formatCellCount(side * side)} pieces
					</span>
				</div>
				<input
					type="range"
					min={MIN_SIDE}
					max={MAX_SIDE}
					step={SIDE_STEP}
					value={side}
					onChange={(e) => onSideChange(parseInt(e.target.value, 10))}
					className="mt-2 w-full accent-slate-700"
				/>
			</div>

			<div className="mx-4 shrink-0 border-slate-100 border-t" />

			<div className="flex min-h-0 flex-1 flex-col">
				<div className="flex shrink-0 items-center justify-between px-4 pt-4 pb-1">
					<label className="block font-medium text-slate-400 text-xs uppercase tracking-wider">
						Pieces
					</label>
					{pieces.length > 0 && (
						<Button onClick={onClearPieces} className="text-xs uppercase">
							Clear all
						</Button>
					)}
				</div>

				<div
					className={cn(
						"overflow-y-auto px-4",
						addingPiece ? "max-h-[50%]" : "min-h-0 flex-1",
					)}
				>
					{pieces.map((entry, i) => (
						<div
							key={i}
							className="flex items-center gap-2 rounded-md px-2 py-1.5 text-slate-700"
						>
							<PieceInfo kind={entry.kind} color={colorAt(entry.colorIndex)} />
							<Button
								onClick={() => onRemovePiece(i)}
								className="size-7 rounded-full"
							>
								<XIcon size={12} />
							</Button>
						</div>
					))}
					{!addingPiece && !allColorsUsed && (
						<Button
							variant="outline"
							onClick={openAdder}
							className="my-2 w-full"
						>
							<PlusIcon size={14} />
							Add piece
						</Button>
					)}
				</div>

				{addingPiece && (
					<div className="flex min-h-0 flex-1 flex-col border-slate-100 border-t">
						<div className="flex shrink-0 items-center gap-1.5 p-4">
							{PALETTE.map((c, i) => {
								const used = usedColorIndices.has(i);
								const selected = i === adderColorIndex;
								return (
									<button
										key={i}
										disabled={used}
										onClick={() => setAdderColorIndex(i)}
										className={cn(
											"flex size-5 shrink-0 items-center justify-center rounded-full outline-offset-2 transition",
											used && "cursor-not-allowed opacity-30",
											!used && !selected && "hover:scale-110",
										)}
										style={{
											background: c,
											...(selected && { outline: `2px solid ${c}` }),
										}}
									>
										{selected && (
											<CheckIcon
												size={9}
												className="text-white"
												strokeWidth={3}
											/>
										)}
									</button>
								);
							})}
							<button
								onClick={() => setAddingPiece(false)}
								className="ml-auto text-slate-400 text-xs transition-colors hover:text-slate-600"
							>
								Cancel
							</button>
						</div>
						<div className="min-h-0 flex-1 overflow-y-auto px-4 pb-2">
							{PIECE_KINDS.map((kind) => (
								<button
									key={kind}
									onClick={() => {
										onAddPiece(kind, adderColorIndex);
										setAddingPiece(false);
									}}
									className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-slate-600 text-sm transition-colors hover:bg-slate-50 hover:text-slate-800"
								>
									<PieceInfo kind={kind} color={adderColor} />
								</button>
							))}
						</div>
					</div>
				)}
			</div>

			<div className="grid grid-cols-2 gap-2 border-slate-100 border-t px-4 py-3">
				<Button variant="secondary" onClick={onRandom} disabled={generating}>
					<ShuffleIcon size={14} />
					Random
				</Button>
				<Button
					variant="primary"
					onClick={onGenerate}
					disabled={generating || pieces.length === 0}
				>
					<RefreshCwIcon size={14} />
					Generate
				</Button>
			</div>
		</>
	);
}

function Help() {
	const running = new Uint8Array(HELP_SIDE * HELP_SIDE);
	const steps = HELP_STEPS.map(([idx, val, caption]) => {
		running[idx] = val;
		return { board: new Uint8Array(running), current: idx, caption };
	});

	return (
		<div className="flex-1 space-y-4 overflow-y-auto px-5 py-5 text-slate-600 text-sm leading-relaxed">
			<p>
				Squares spiral outward from the centre. A piece skips squares attacked
				by a <em>different</em> colour, but its own attacks are allowed.
			</p>

			<p className="text-slate-500">
				Two knights take turns below:{" "}
				<strong className="text-slate-800">black</strong>, then{" "}
				<strong style={{ color: PALETTE[1] }}>red</strong>.
			</p>
			<div className="flex items-center justify-center gap-4 rounded-md px-3 py-2.5">
				<span className="font-medium text-slate-600 text-xs">
					♞ Knight moves:
				</span>
				<MoveGrid
					offsets={PIECES.knight.offsets}
					color={PALETTE[0]}
					radius={2}
				/>
			</div>
			<div className="space-y-3">
				{steps.map(({ board, current, caption }, i) => (
					<div key={i} className="flex items-center gap-3">
						<BoardPreview
							side={HELP_SIDE}
							board={board}
							step={i + 1}
							currentIndex={current}
						/>
						<p className="flex-1 text-slate-500 text-xs leading-snug">
							{caption}
						</p>
					</div>
				))}
			</div>
		</div>
	);
}

type HistoryStripProps = {
	open: boolean;
	entries: HistoryEntry[];
	activeSignature: string | null;
	disabled: boolean;
	onSelect: (entry: HistoryEntry) => void;
	onClear: () => void;
	onClose: () => void;
};

function HistoryStrip({
	open,
	entries,
	activeSignature,
	disabled,
	onSelect,
	onClear,
	onClose,
}: HistoryStripProps) {
	return (
		<div
			className="grid shrink-0 transition-all duration-300 ease-in-out"
			style={{ gridTemplateRows: open ? "1fr" : "0fr" }}
		>
			<div className="min-h-0 overflow-hidden">
				<div className="border-slate-200 border-t bg-white">
					<div className="flex shrink-0 items-center justify-between px-4 py-2">
						<span className="font-semibold text-slate-700 text-sm">
							History
						</span>
						<div className="flex items-center gap-1">
							{entries.length > 0 && (
								<Button onClick={onClear} className="text-xs uppercase">
									Clear
								</Button>
							)}
							<Button onClick={onClose} className="size-7">
								<XIcon size={16} />
							</Button>
						</div>
					</div>
					<div className="flex gap-3 overflow-x-auto px-4 pb-3">
						{entries.map((entry) => {
							const isActive = entry.signature === activeSignature;
							return (
								<button
									key={entry.id}
									disabled={disabled}
									onClick={() => onSelect(entry)}
									className="group flex shrink-0 flex-col items-center gap-1 rounded-lg p-1.5 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
								>
									<img
										src={entry.thumbnail}
										width={60}
										height={60}
										className={cn(
											"rounded-md transition-shadow",
											isActive
												? "ring-2 ring-slate-700 ring-offset-1"
												: "ring-1 ring-slate-200 group-hover:ring-slate-400",
										)}
										alt=""
									/>
									<div className="flex max-w-16 flex-wrap items-center justify-center gap-0.5">
										{entry.pieces.map((p, i) => (
											<span
												key={i}
												className="text-[10px] leading-none"
												style={{
													color: colorAt(p.colorIndex),
												}}
											>
												{PIECES[p.kind].symbol}
											</span>
										))}
									</div>
									<span className="font-mono text-[10px] text-slate-400">
										{entry.side}×{entry.side}
									</span>
								</button>
							);
						})}
					</div>
				</div>
			</div>
		</div>
	);
}

function useBoard() {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const rafRef = useRef<number | null>(null);
	const [generating, setGenerating] = useState(false);
	const [renderedSig, setRenderedSig] = useState<string | null>(null);

	const captureThumb = useCallback((size = THUMB_PX) => {
		const canvas = canvasRef.current;
		if (!canvas) return null;
		const tempCanvas = document.createElement("canvas");
		tempCanvas.width = size;
		tempCanvas.height = size;
		const ctx = tempCanvas.getContext("2d");
		if (!ctx) return null;
		ctx.drawImage(canvas, 0, 0, size, size);
		return tempCanvas.toDataURL("image/webp", 0.5);
	}, []);

	const fill = useCallback((side: number, pieces: PieceEntry[]) => {
		if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
		rafRef.current = null;

		const canvas = canvasRef.current;
		const ctx = canvas?.getContext("2d");
		if (!canvas || !ctx) return;

		const totalCells = side * side;
		const board = new Uint8Array(totalCells);
		const half = (side - 1) / 2;
		const cellPx = CANVAS_PX / side;
		const dots = side < DIRECT_RENDER_MIN_SIDE;
		const dotRadius = Math.max(MIN_DOT_RADIUS, cellPx * DOT_RADIUS_RATIO);
		const edge = (i: number) => Math.floor((i * CANVAS_PX) / side);

		const paint = (col: number, row: number, color: string) => {
			ctx.fillStyle = color;
			if (dots) {
				ctx.beginPath();
				ctx.arc(
					(col + 0.5) * cellPx,
					(row + 0.5) * cellPx,
					dotRadius,
					0,
					Math.PI * 2,
				);
				ctx.fill();
			} else {
				const x = edge(col);
				const y = edge(row);
				ctx.fillRect(x, y, edge(col + 1) - x, edge(row + 1) - y);
			}
		};

		ctx.setTransform(1, 0, 0, 1, 0, 0);
		ctx.clearRect(0, 0, CANVAS_PX, CANVAS_PX);
		if (dots) {
			for (let row = 0; row < side; row++) {
				for (let col = 0; col < side; col++) paint(col, row, COLOR_EMPTY);
			}
		} else {
			ctx.fillStyle = COLOR_EMPTY;
			ctx.fillRect(0, 0, CANVAS_PX, CANVAS_PX);
		}

		if (pieces.length === 0) {
			setGenerating(false);
			return;
		}

		const n = pieces.length;
		const coords = buildSpiralCoords(totalCells);
		const attackBits = new Uint32Array(totalCells);
		const cursors = new Int32Array(n);
		const offsetsByPiece: Coord[][] = pieces.map((e) =>
			PIECES[e.kind].offsets.map(([dx, dy]): Coord => [dx, -dy]),
		);

		const active = Array.from({ length: n }, (_, i) => i);
		let turn = 0;

		setGenerating(true);
		const budget = Math.max(1, Math.ceil((totalCells / TARGET_FRAMES) * n));

		const animate = () => {
			const deadline = performance.now() + FRAME_BUDGET_MS;
			let processed = 0;

			while (processed < budget && active.length > 0) {
				if (
					(processed & 511) === 0 &&
					processed > 0 &&
					performance.now() >= deadline
				)
					break;
				processed++;

				const slot = active[turn] ?? 0;
				const cur = cursors[slot] ?? 0;
				if (cur >= totalCells) {
					active.splice(turn, 1);
					if (active.length > 0) turn = turn % active.length;
					continue;
				}
				cursors[slot] = cur + 1;
				const coord = coords[cur];
				const entry = pieces[slot];
				if (!coord || !entry) continue;

				const [x, y] = coord;
				const col = x + half;
				const row = half - y;
				const cellIdx = row * side + col;
				if ((board[cellIdx] ?? 0) !== 0) continue;

				const myBit = 1 << slot;
				const bits = attackBits[cellIdx] ?? 0;
				if (n === 1 ? bits !== 0 : (bits & ~myBit) !== 0) continue;

				board[cellIdx] = entry.colorIndex + 1;
				paint(col, row, colorAt(entry.colorIndex));

				for (const [dc, dr] of offsetsByPiece[slot] ?? []) {
					const nc = col + dc;
					const nr = row + dr;
					if (nc >= 0 && nc < side && nr >= 0 && nr < side) {
						const ni = nr * side + nc;
						attackBits[ni] = (attackBits[ni] ?? 0) | myBit;
					}
				}
				turn = (turn + 1) % active.length;
			}

			if (active.length > 0) {
				rafRef.current = requestAnimationFrame(animate);
				return;
			}
			setGenerating(false);
			setRenderedSig(patternSignature(side, pieces));
		};

		rafRef.current = requestAnimationFrame(animate);
	}, []);

	return { canvasRef, fill, generating, renderedSig, captureThumb };
}

export default function App() {
	const [view, setView] = useState<SidebarView>("settings");
	const [sidebarOpen, setSidebarOpen] = useState(false);
	const [hasOpened, setHasOpened] = useState(false);
	const [side, setSide] = useState(INITIAL_SIDE);
	const [pieces, setPieces] = useState<PieceEntry[]>([
		{ kind: "knight", colorIndex: 0 },
		{ kind: "knight", colorIndex: 1 },
	]);
	const isMobile = useIsMobile();
	const { canvasRef, fill, generating, renderedSig, captureThumb } = useBoard();
	const hasPattern = renderedSig === patternSignature(side, pieces);

	const [history, setHistory] = usePersistedState<HistoryEntry[]>(
		HISTORY_STORAGE_KEY,
		[],
	);
	const [historyOpen, setHistoryOpen] = useState(false);
	const lastFillRef = useRef<{
		side: number;
		pieces: PieceEntry[];
	} | null>(null);

	const generate = useCallback(
		(side: number, pieces: PieceEntry[]) => {
			lastFillRef.current = { side, pieces };
			fill(side, pieces);
		},
		[fill],
	);

	useEffect(() => {
		const config = lastFillRef.current;
		if (!config || !renderedSig || generating) return;
		if (!config) return;
		const thumbnail = captureThumb();
		if (!thumbnail) return;
		setHistory((prev) => {
			if (prev.some(({ signature }) => signature === renderedSig)) return prev;
			return [
				...prev,
				{
					id: Date.now(),
					side: config.side,
					pieces: config.pieces,
					thumbnail,
					signature: renderedSig,
				},
			].slice(-MAX_HISTORY);
		});
	}, [renderedSig, generating, captureThumb, setHistory]);

	const toggleSidebar = (view: SidebarView) => {
		setView((prev) => {
			setSidebarOpen(prev === view ? !sidebarOpen : true);
			return view;
		});
		setHasOpened(true);
	};

	const closeSidebar = () => setSidebarOpen(false);

	const addPiece = (kind: PieceKind, colorIndex: number) =>
		setPieces((pieces) => [...pieces, { kind, colorIndex }]);
	const removePiece = (i: number) =>
		setPieces((pieces) => pieces.filter((_, j) => j !== i));
	const clearPieces = () => setPieces([]);

	const handleGenerate = () => {
		generate(side, pieces);
		if (isMobile) closeSidebar();
	};

	const generateRandom = () => {
		const count = 2 + Math.floor(Math.random() * 5);
		const colors = Array.from({ length: PALETTE.length }, (_, i) => i)
			.sort(() => Math.random() - 0.5)
			.slice(0, count);
		const newPieces: PieceEntry[] = colors.map((colorIndex) => ({
			kind:
				PIECE_KINDS[Math.floor(Math.random() * PIECE_KINDS.length)] ?? "knight",
			colorIndex,
		}));
		setPieces(newPieces);
		generate(side, newPieces);
		if (isMobile) closeSidebar();
	};

	const selectHistory = (entry: HistoryEntry) => {
		setPieces(entry.pieces);
		setSide(entry.side);
		generate(entry.side, entry.pieces);
	};

	const clearHistory = () => {
		setHistory([]);
		setHistoryOpen(false);
	};

	const showOverlay = !generating && !hasPattern && pieces.length > 0;

	return (
		<div className="flex h-screen overflow-hidden bg-white">
			<style>{`
				@keyframes hint-nudge {
					0%, 100% { transform: translateX(0); }
					50% { transform: translateX(6px); }
				}
				.animate-hint-nudge { animation: hint-nudge 1.4s ease-in-out infinite; }
				 `}</style>
			<div className="flex min-w-0 flex-1 flex-col">
				<div className="relative flex min-h-0 min-w-0 flex-1 items-center justify-center">
					<canvas
						ref={canvasRef}
						width={CANVAS_PX}
						height={CANVAS_PX}
						className="block aspect-square max-h-full max-w-full"
					/>

					{showOverlay && (
						<div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
							<Button
								variant="primary"
								onClick={() => generate(side, pieces)}
								className="px-4 py-2 shadow-sm"
							>
								<RefreshCwIcon size={14} />
								Generate
							</Button>
						</div>
					)}

					<div className="absolute top-4 right-4 z-20 flex items-start gap-2">
						{!hasOpened && (
							<div className="flex h-9 animate-hint-nudge items-center">
								<div className="whitespace-nowrap rounded-md bg-slate-900 px-3 py-1.5 font-medium text-white text-xs shadow-lg">
									Click here to select pieces
								</div>
								<div className="size-0 border-y-[6px] border-y-transparent border-l-[6px] border-l-slate-900" />
							</div>
						)}
						<div className="flex flex-col gap-2">
							<Button
								variant="outline"
								className="size-9"
								onClick={() => toggleSidebar("settings")}
							>
								<SettingsIcon size={18} />
							</Button>
							<Button
								variant="outline"
								className="size-9"
								onClick={() => toggleSidebar("help")}
							>
								<HelpCircleIcon size={18} />
							</Button>
						</div>
					</div>

					{history.length > 0 && (
						<div className="absolute bottom-4 left-4 z-20">
							<Button
								variant="outline"
								className="size-9"
								onClick={() => setHistoryOpen(!historyOpen)}
							>
								<ClockIcon size={18} />
							</Button>
						</div>
					)}
				</div>

				<HistoryStrip
					open={historyOpen}
					entries={history}
					activeSignature={renderedSig}
					disabled={generating}
					onSelect={selectHistory}
					onClear={clearHistory}
					onClose={() => setHistoryOpen(false)}
				/>
			</div>

			<Sidebar
				open={sidebarOpen}
				isMobile={isMobile}
				title={view === "settings" ? "Settings" : "Help"}
				onClose={closeSidebar}
			>
				{view === "settings" ? (
					<Settings
						side={side}
						onSideChange={setSide}
						pieces={pieces}
						onAddPiece={addPiece}
						onRemovePiece={removePiece}
						onClearPieces={clearPieces}
						onGenerate={handleGenerate}
						onRandom={generateRandom}
						generating={generating}
					/>
				) : (
					<Help />
				)}
			</Sidebar>
		</div>
	);
}

type SidebarView = "settings" | "help";

type Coord = [number, number];

type PieceEntry = {
	kind: PieceKind;
	colorIndex: number;
};

type HistoryEntry = {
	id: number;
	side: number;
	pieces: PieceEntry[];
	thumbnail: string;
	signature: string;
};

const INITIAL_SIDE = 701;
const MIN_SIDE = 11;
const MAX_SIDE = 1789;
const SIDE_STEP = 2;
const TARGET_FRAMES = 100;
const FRAME_BUDGET_MS = 14;
const DOT_RADIUS_RATIO = 0.38;
const MIN_DOT_RADIUS = 1;
const CANVAS_PX = 4096;
const DIRECT_RENDER_MIN_SIDE = 300;
const MAX_HISTORY = 12;
const THUMB_PX = 128;
const HISTORY_STORAGE_KEY = "chess-patterns";

const HELP_SIDE = 5;
const HELP_STEPS: [index: number, value: number, caption: string][] = [
	[12, 1, "Black knight at the centre."],
	[13, 2, "Red takes the next spiral square."],
	[8, 1, "Black continues outward."],
	[7, 2, "Red follows. No conflicts yet."],
	[11, 1, "Black skips this square. Red attacks it."],
	[6, 2, "Red lands here. Own attacks are allowed."],
	[19, 1, "Black skips three blocked squares."],
	[16, 2, "Red on the opposite side."],
	[9, 1, "Black reaches the far edge."],
	[14, 2, "Red fills in a gap."],
];

const COLOR_EMPTY = "#f1f5f9";
const PALETTE = [
	"#1d293d",
	"#fb2c36",
	"#2b7fff",
	"#00c950",
	"#ad46ff",
	"#ff6900",
	"#00b8db",
	"#f6339a",
	"#f0b100",
	"#00bba7",
] as const;

const PIECE_KINDS = [
	"knight",
	"ferz",
	"wazir",
	"king",
	"dabbaba",
	"alfil",
	"dromedary",
	"camel",
	"zebra",
	"anthelope",
] as const;

type PieceKind = (typeof PIECE_KINDS)[number];

const PIECES: Record<
	PieceKind,
	{ symbol: ReactNode; offsets: readonly Coord[] }
> = {
	knight: { symbol: "♞", offsets: leaperOffsets([2, 1]) },
	ferz: { symbol: scSymbol("♝", "-1"), offsets: leaperOffsets([1, 1]) },
	wazir: { symbol: scSymbol("♜", "-1"), offsets: leaperOffsets([1, 0]) },
	king: { symbol: "♚", offsets: leaperOffsets([1, 0], [1, 1]) },
	dabbaba: { symbol: "⊞", offsets: leaperOffsets([2, 0]) },
	alfil: { symbol: "⊠", offsets: leaperOffsets([2, 2]) },
	dromedary: { symbol: "⊕", offsets: leaperOffsets([3, 0]) },
	camel: { symbol: scSymbol("♞", "-1"), offsets: leaperOffsets([3, 1]) },
	zebra: { symbol: "♘", offsets: leaperOffsets([3, 2]) },
	anthelope: { symbol: scSymbol("♘", "-1 -1"), offsets: leaperOffsets([4, 3]) },
};

const MAX_LEAP_RADIUS = Math.max(
	1,
	...PIECE_KINDS.flatMap((k) =>
		PIECES[k].offsets.flatMap(([dx, dy]) => [Math.abs(dx), Math.abs(dy)]),
	),
);

function usePersistedState<T>(key: string, initial: T) {
	const [value, setValue] = useState<T>(() => {
		try {
			const stored = localStorage.getItem(key);
			return stored ? (JSON.parse(stored) as T) : initial;
		} catch {
			return initial;
		}
	});
	useEffect(() => {
		try {
			localStorage.setItem(key, JSON.stringify(value));
		} catch {}
	}, [key, value]);
	return [value, setValue] as const;
}

function useIsMobile(breakpoint = 768) {
	const query = `(max-width: ${breakpoint - 1}px)`;
	const [mobile, setMobile] = useState(() => matchMedia(query).matches);
	useEffect(() => {
		const mq = matchMedia(query);
		const handler = (e: MediaQueryListEvent) => setMobile(e.matches);
		mq.addEventListener("change", handler);
		return () => mq.removeEventListener("change", handler);
	}, [query]);
	return mobile;
}

function buildSpiralCoords(count: number) {
	const result: Coord[] = [[0, 0]];
	let x = 0,
		y = 0,
		step = 1,
		n = 1;
	while (n < count) {
		for (let i = 0; i < step && n < count; i++, n++) result.push([++x, y]);
		for (let i = 0; i < step && n < count; i++, n++) result.push([x, ++y]);
		step++;
		for (let i = 0; i < step && n < count; i++, n++) result.push([--x, y]);
		for (let i = 0; i < step && n < count; i++, n++) result.push([x, --y]);
		step++;
	}
	return result;
}

function leaperOffsets(...pairs: Coord[]) {
	const seen = new Map<string, Coord>();
	for (const [a, b] of pairs) {
		const variants: Coord[] = [
			[a, b],
			[a, -b],
			[-a, b],
			[-a, -b],
			[b, a],
			[b, -a],
			[-b, a],
			[-b, -a],
		];
		for (const [dx, dy] of variants) {
			if (dx || dy) seen.set(`${dx},${dy}`, [dx, dy]);
		}
	}
	return [...seen.values()];
}

function formatCellCount(n: number) {
	if (n < 1_000_000) return n.toLocaleString();
	return `${+(n / 1_000_000).toFixed(1)} million`;
}

function colorAt(i: number) {
	return PALETTE[i % PALETTE.length] ?? PALETTE[0];
}

function cn(...classes: (string | false | null | undefined)[]) {
	return classes.filter(Boolean).join(" ");
}

function patternSignature(side: number, pieces: PieceEntry[]) {
	return `${side}:${pieces.map(({ kind, colorIndex }) => `${kind}/${colorIndex}`).join(",")}`;
}

function scSymbol(chat: string, scale: string) {
	return (
		<span className="inline-block" style={{ scale }}>
			{chat}
		</span>
	);
}
